"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { loginApi } from "../api/loginApi"
import { apiFetch } from "../api/apiClient"
import "../styles/company-login.css"
import { FORGOT_PASSWORD_PATH, HOME_PATH } from "../navigation/paths"

export default function CompanyLogin() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  })
  const [companies, setCompanies] = useState([])
  const [selectedCompany, setSelectedCompany] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await apiFetch("/api/companies/public/list", { skipAuth: true })
        if (response.ok) {
          const data = await response.json()
          if (data.success && Array.isArray(data.data)) {
            setCompanies(data.data)
          }
        }
      } catch (err) {
        console.error("Failed to load companies:", err)
      }
    }
    fetchCompanies()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    // Clear error when user starts typing
    if (error) setError("")
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    try {
      if (!selectedCompany) {
        setError("Please select a company")
        setLoading(false)
        return
      }

      if (!formData.email || !formData.password) {
        setError("Please fill in all fields")
        setLoading(false)
        return
      }

      const response = await loginApi.login(formData.email, formData.password, selectedCompany)

      if (response.success) {
        // Navigate to dashboard after successful login
        navigate(HOME_PATH)
      } else {
        setError(response.message || "Login failed")
      }
    } catch (err) {
      setError(err.message || "An error occurred during login")
    } finally {
      setLoading(false)
    }
  }

  const toggleTheme = () => {
    // Implement theme toggle logic here
  }

  return (
    <div className="company-login-wrapper">
      {/* Navbar */}
      {/* <nav className="navbar navbar-expand-lg navbar-light headback shadow-sm fixed-top">
        <div className="container">
          <a className="navbar-brand fw-bold" href="/">
            <img src="/images/logo.png" alt="Logo" />
          </a>
          <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav" aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse justify-content-center" id="navbarNav">
            <ul className="navbar-nav mx-auto">
              <li className="nav-item">
                <a className="nav-link" href="/">
                  Home
                </a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#features">
                  Features
                </a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#how">
                  How It Works
                </a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#contact">
                  Contact
                </a>
              </li>
            </ul>
            <div className="d-flex flex-wrap">
              <a className="btn btn-turquoise px-2" href="/super-admin-login">
                Super Admin
              </a>
              <button className="btn btn-primary-navy ms-2">Request Demo</button>
              <button id="themeToggle" className="theme-toggle ms-2" title="Toggle Dark/Light" onClick={toggleTheme}>
                <i className="bi bi-moon-stars-fill"></i>
              </button>
            </div>
          </div>
        </div>
      </nav> */}

      {/* Login Form Section */}
      <section className="features py-5">
        <div className="container-lg">
          <div className="row g-4 justify-content-center">
            {/* Login Form Card */}
            <div className="col-lg-5">
              <div className="contact-card destination">
                <h4 className="fw-bold mb-3">Login to Company</h4>

                {/* Error Banner */}
                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button type="button" className="btn-close" aria-label="Dismiss error" onClick={() => setError("")}></button>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="login-select-company">Select Company</label>
                    <select id="login-select-company"
                      className="form-select form-control"
                      value={selectedCompany}
                      onChange={(e) => setSelectedCompany(e.target.value)}
                      disabled={loading}
                      required
                    >
                      <option value="">-- Choose Company --</option>
                      {companies.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.companyName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label" htmlFor="login-email">Email</label>
                    <input id="login-email"
                      type="email"
                      name="email"
                      className="form-control"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label" htmlFor="login-password">Password</label>
                    <div className="input-group">
                      <input id="login-password"
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className="form-control"
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                        title={showPassword ? "Hide password" : "Show password"}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        aria-pressed={showPassword}
                      >
                        <i className={`fe ${showPassword ? "fe-eye-off" : "fe-eye"}`} aria-hidden="true"></i>
                      </button>
                    </div>
                  </div>

                  <div className="text-end mb-3">
                    <a
                      href={FORGOT_PASSWORD_PATH}
                      className="text-decoration-none fw-500 login-forgot-link"
                    >
                      Forgot Password?
                    </a>
                  </div>

                  <button type="submit" className="btn btn-primary-navy w-100" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Logging in...
                      </>
                    ) : (
                      "Login"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
